import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Chip, FormControlLabel, Stack, Switch, Tab, Tabs } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { FakeButtonLink } from '../../../../components/FakeLink';
import { Field, FieldGrid, SectionCard } from '../../../../components/Section';
import { TREASURY_OPERATION_TAB_LABELS, TreasuryOperationTab } from '../../../../constants/treasury';
import { usePermissions, useSearchParamsState } from '../../../../hooks';
import { useUpdateTreasuryReinvestDividendMutation } from '../../../../redux/features/treasury/treasury.apiSlice';
import { locations } from '../../../../routes/locations';
import { TreasuryAlpacaAccount, TreasuryCompanySetup } from '../../../../types/treasury';
import * as permissions from '../../../../utils/permissions';
import { getTreasuryErrorMessage } from '../../../../utils/treasury';
import { TreasuryContributionsTab } from './TreasuryContributionsTab';
import { TreasuryRedeemRulesTab } from './TreasuryRedeemRulesTab';
import { TreasuryPortfolioSummary } from './TreasuryPortfolioSummary';
import { TreasuryRedemptionTab } from './TreasuryRedemptionTab';
import { TreasuryWithdrawalsTab } from './TreasuryWithdrawalsTab';
import { useTreasuryPortfolios } from './useTreasuryPortfolios';

type TreasuryOperationsProps = {
  setup: TreasuryCompanySetup;
  account: TreasuryAlpacaAccount;
};

/**
 * The treasury once the company Alpaca account is ACTIVE — the day-to-day money: contributions in, redemptions and
 * redemption rules out of the portfolio, withdrawals out of the treasury bank. The tab is kept in the URL.
 */
export const TreasuryOperations = ({ setup, account }: TreasuryOperationsProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const canUpdateSetup = usePermissions({ items: [permissions.Treasury.update] });
  const canReadContributions = usePermissions({ items: [permissions.Treasury.readContributions] });
  const canCreateRedemption = usePermissions({ items: [permissions.Treasury.createRedemption] });
  const canReadRules = usePermissions({ items: [permissions.Treasury.readRedeemRules] });
  const canReadWithdrawals = usePermissions({ items: [permissions.Treasury.readWithdrawals] });
  const [tab, setTab] = useSearchParamsState('tab', TreasuryOperationTab.Contributions);
  const [reinvestDividend, setReinvestDividend] = useState(setup.reinvestDividend);
  const [updateReinvestDividend, { isLoading: isSavingReinvest }] = useUpdateTreasuryReinvestDividendMutation();
  const ownerUserId = setup.employee?.external_user_id ?? null;
  const { portfolios, isLoading: isPortfoliosLoading } = useTreasuryPortfolios(ownerUserId);
  const { companyId } = setup;

  const toggleReinvest = async (next: boolean) => {
    try {
      const response = await updateReinvestDividend({ companyId, reinvestDividend: next }).unwrap();
      setReinvestDividend(response?.reinvestDividend ?? next);
      enqueueSnackbar(next ? 'Dividend reinvest is on' : 'Dividend reinvest is off', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getTreasuryErrorMessage(error, 'Failed to update the dividend setting'), { variant: 'error' });
    }
  };

  const tabAllowed: Record<TreasuryOperationTab, boolean> = {
    [TreasuryOperationTab.Contributions]: canReadContributions,
    [TreasuryOperationTab.Redemptions]: canCreateRedemption,
    [TreasuryOperationTab.RedemptionRules]: canReadRules,
    [TreasuryOperationTab.Withdrawals]: canReadWithdrawals,
  };
  const tabs = Object.values(TreasuryOperationTab).filter((value) => tabAllowed[value]);
  const currentTab = tabs.includes(tab as TreasuryOperationTab) ? (tab as TreasuryOperationTab) : tabs[0];

  return (
    <Stack spacing={2}>
      <SectionCard
        title="Company Account"
        description="Money moves only while this account is ACTIVE."
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" color="success" label={account.status} />
            {ownerUserId && (
              <FakeButtonLink startIcon={<OpenInNewIcon />} href={locations.user(ownerUserId)}>
                Owner #{ownerUserId}
              </FakeButtonLink>
            )}
          </Stack>
        }
      >
        <FieldGrid columns={4}>
          <Field label="Alpaca Account" value={account.accountId} />
          <Field label="Owner" value={setup.employee?.full_name_en ?? '—'} />
          <Field label="Group" value={setup.group.name} />
          <Field
            label="Dividends"
            value={
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={reinvestDividend}
                    disabled={!canUpdateSetup || isSavingReinvest}
                    onChange={(event) => toggleReinvest(event.target.checked)}
                  />
                }
                label={reinvestDividend ? 'Reinvest on' : 'Reinvest off'}
              />
            }
          />
        </FieldGrid>
      </SectionCard>

      <TreasuryPortfolioSummary companyId={companyId} />

      <Tabs
        value={currentTab}
        onChange={(_, next: TreasuryOperationTab) => setTab(next)}
        variant="scrollable"
        allowScrollButtonsMobile
      >
        {tabs.map((value) => (
          <Tab key={value} value={value} label={TREASURY_OPERATION_TAB_LABELS[value]} />
        ))}
      </Tabs>

      {currentTab === TreasuryOperationTab.Contributions && <TreasuryContributionsTab companyId={companyId} />}
      {currentTab === TreasuryOperationTab.Redemptions && (
        <TreasuryRedemptionTab
          companyId={companyId}
          ownerUserId={ownerUserId}
          portfolios={portfolios}
          isPortfoliosLoading={isPortfoliosLoading}
        />
      )}
      {currentTab === TreasuryOperationTab.RedemptionRules && (
        <TreasuryRedeemRulesTab
          companyId={companyId}
          portfolios={portfolios}
          isPortfoliosLoading={isPortfoliosLoading}
        />
      )}
      {currentTab === TreasuryOperationTab.Withdrawals && <TreasuryWithdrawalsTab companyId={companyId} />}
    </Stack>
  );
};
